/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, warp-drive/no-legacy-request-patterns */
import Model, {
  attr,
  belongsTo,
  hasMany,
  type AsyncBelongsTo,
  type AsyncHasMany,
} from '@warp-drive/legacy/model';
import { computed } from '@ember/object';
import { union, alias } from '@ember/object/computed';
import { service } from '@ember/service';
import { isEmpty } from '@ember/utils';
import type { Type } from '@warp-drive/core/types/symbols';
import type { FragmentArray } from 'ember-data-model-fragments/array/fragment';
import { fragmentArray } from 'ember-data-model-fragments/attributes';
import type { EnumValue } from '@linc-technologies/linced-common/enums/base-enum';
import type Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import {
  SuspensionState,
  SuspensionReason,
  SuspensionCategory,
  ApprovalReason,
  SuspensionTabs,
} from '@linc-technologies/linced-common/enums/suspensions';
import { resolved } from '@linc-technologies/linced-common/utils/arrays';
import moment from 'moment';
import ContactType from 'linced-app/enums/contact-type';
import type BehaviourIncident from 'linced-app/models/attendance/behaviour-incident';
import type Expulsion from 'linced-app/models/attendance/expulsion';
import type Media from 'linced-app/models/media';
import type Person from 'linced-app/models/person';
import type Post from 'linced-app/models/posts/post';
import type School from 'linced-app/models/school';
import type ApprovalRequest from 'linced-app/models/wellbeing/approval-request';
import type Extension from 'linced-app/models/wellbeing/extension';
import type BehaviourDatesService from 'linced-app/services/behaviour-dates';
import type DatesService from 'linced-app/services/dates';

// Proto: attendance.Suspension.
export default class Suspension extends Model {
  declare [Type]: 'attendance/suspension';

  @service
  declare dates: DatesService;

  @service
  declare behaviourDates: BehaviourDatesService;

  // externalId is the suspension's id in a 3rd party database
  @attr('string', { defaultValue: '' })
  declare externalId: string;

  // createTime is when the resource was created in seconds from the epoch.
  @attr('number')
  declare createTime: number | undefined;

  // updateTime is the last time the resource was modified in seconds from
  // the epoch.
  @attr('number')
  declare updateTime: number | undefined;

  @alias('schoolId')
  declare school: unknown;

  // schoolId is the school the suspension is assigned to.
  @belongsTo('school', { async: true, inverse: null })
  declare schoolId: AsyncBelongsTo<School>;

  // recordedDate contains the date the suspension was recorded
  @attr('string')
  declare recordedDate: string | undefined;

  // startDate contains the date the suspension will begin
  @attr('string')
  declare startDate: string | undefined;

  // endDate contains the date the suspension will end
  @attr('string')
  declare endDate: string | undefined;

  // earlyReturnDate contains an agreed date the student will return on if
  // the suspension ends early
  @attr('string')
  declare earlyReturnDate: string | undefined;

  @alias('learnerId')
  declare learner: AsyncBelongsTo<Person>;

  // learnerId is the unique identifier of the learner who the suspension relates to.
  @belongsTo('person', { async: true, inverse: null })
  declare learnerId: AsyncBelongsTo<Person>;

  @alias('learner.id')
  declare learner_id: string | null | undefined;

  // category is the category this suspension falls under - this is where formal warnings
  // are determined
  @attr('number', { defaultValue: 0 })
  declare category: number;

  // reason is a reason for the suspension selected from a predefined list
  @attr('number', { defaultValue: 0 })
  declare reason: number;

  // written reason is any additional information that is added when a suspensions reason is "other"
  @attr('string', { defaultValue: '' })
  declare writtenReason: string;

  // consultationDate is the date of the suspension consultation with the parents
  @attr('string')
  declare consultationDate: string | undefined;

  // duration is the intended number of school days for the suspension to last for
  @attr('number', { defaultValue: 0 })
  declare duration: number;

  // extended is a boolean that allows tracing suspensions that have been extended beyond the limit
  @attr('boolean', { defaultValue: false })
  declare extended: boolean;

  // needsApproval is a boolean that allows filtering suspensions that currently require approval by the DEL
  @attr('boolean', { defaultValue: false })
  declare needsApproval: boolean;

  // approvals is an array of approval objects that store a reason, time, and whether or not the request was approved
  @fragmentArray('wellbeing/approval-request')
  declare approvals: FragmentArray<ApprovalRequest>;

  // behaviourDetails contains details on the learners behaviour relating to the suspension
  @attr('string', { defaultValue: '' })
  declare behaviourDetails: string;

  // behaviourExpectations contains details on the expected behaviour of the learner
  @attr('string', { defaultValue: '' })
  declare behaviourExpectations: string;

  // description (deprecated) contains details of the suspension itself
  @attr('string', { defaultValue: '' })
  declare description: string;

  // extensions contains an array to track the times the suspension was extended
  @fragmentArray('wellbeing/extension')
  declare extensions: FragmentArray<Extension>;

  @hasMany('posts/post', { async: true, inverse: null })
  declare extensionLetterIds: AsyncHasMany<Post>;

  @alias('extensionLetterIds')
  declare extensionLetters: AsyncHasMany<Post>;

  // nominated contact is the ID of a staff member assigned to the communication with caregivers
  @belongsTo('person', { async: true, inverse: null })
  declare nominatedContact: AsyncBelongsTo<Person>;

  // caregiverNotified is a property that can be used to track whether or not a caregiver has been
  // notified of a recent change
  @attr('boolean', { defaultValue: false })
  declare caregiverNotified: boolean;

  // postId contains the ID of the connected suspension post
  @belongsTo('posts/post', { async: true, inverse: null })
  declare postId: AsyncBelongsTo<Post>;

  @alias('postId')
  declare post: AsyncBelongsTo<Post>;

  // caregiverIds are the caregivers who will be contacted by the school regarding this suspension.
  @hasMany('person', { async: true, inverse: null })
  declare caregiverIds: AsyncHasMany<Person>;

  @alias('caregiverIds')
  declare caregivers: AsyncHasMany<Person>;

  @hasMany('posts/post', { async: true, inverse: null })
  declare letterIds: AsyncHasMany<Post>;

  @alias('letterIds')
  declare letters: AsyncHasMany<Post>;

  // incidentId is the ID of the incident this suspension stemmed from
  @belongsTo('attendance/behaviour-incident', {
    async: true,
    inverse: 'suspensionId',
  })
  declare incidentId: AsyncBelongsTo<BehaviourIncident>;

  // attachments are documents related to the suspension, such as reports or written statements,
  // which can be attached to the post.
  @hasMany('media', { inverse: null, async: true })
  declare attachments: AsyncHasMany<Media>;

  // reviewDate is the date for the school to review the suspension internally
  @attr('string')
  declare reviewDate: string | undefined;

  // jurisdiction is the jurisdiction where this suspension was recorded
  @attr('number', { defaultValue: 0 })
  declare jurisdiction: EnumValue<typeof Jurisdiction> | 0;

  // whether this student was deemed an immediateThreat to the safety of others etc
  @attr('boolean', { defaultValue: false })
  declare immediateThreat: boolean;

  @belongsTo('attendance/expulsion', { async: true, inverse: 'suspensionId' })
  declare expulsionId: AsyncBelongsTo<Expulsion>;

  // behaviourIncident is the ID of the incident this suspension stemmed from
  @alias('incidentId')
  declare behaviourIncident: AsyncBelongsTo<BehaviourIncident>;

  @alias('expulsionId')
  declare expulsion: AsyncBelongsTo<Expulsion>;

  // outcomeDetails are any notes relevant to the outcome of the suspension
  @attr('string', { defaultValue: '' })
  declare outcomeDetails: string;

  // notificationReason provides a set reason for sending DEL notifications
  @attr('number', { defaultValue: 0 })
  declare notificationReason: number;

  @computed('expulsion.id')
  get isExpulsionSuspension() {
    return !isEmpty(this.get('expulsion.id'));
  }

  @computed(
    'isExpulsionSuspension',
    'tab',
    'consultationDate',
    'needsApproval',
    'reviewDateHasPassed',
    'state',
  )
  get actionRequired() {
    if (this.isExpulsionSuspension) {
      return false;
    }
    if (this.state === SuspensionState.FINALISED) {
      return false;
    }
    if (this.tab === SuspensionTabs.PENDING) {
      if (this.category !== 0) {
        // Pending suspensions with a category have been actioned, but require
        // DEL approval before proceeding
        return true;
      }
    }
    if (
      this.tab === SuspensionTabs.FORMAL_CAUTIONS ||
      this.tab === SuspensionTabs.SUSPENSIONS ||
      this.tab === SuspensionTabs.EXTENDED_SUSPENSIONS
    ) {
      return isEmpty(this.consultationDate) || this.needsApproval;
    }
    return false;
  }

  @computed('startDate', 'endDate', 'earlyReturnDate')
  get active() {
    if (this.startDate && this.endDate) {
      const today = moment().format('YYYY-MM-DD');
      if (moment(this.endDate, 'YYYY-MM-DD').isSameOrAfter(today, 'day')) {
        if (this.earlyReturnDate) {
          return moment(this.earlyReturnDate, 'YYYY-MM-DD').isSameOrAfter(
            today,
            'day',
          );
        }
        return true;
      }
    }
    return false;
  }

  // .content is the loaded ManyArray: the array macros ignore a
  // PromiseManyArray, which is not an array.
  @union('letters.content', 'extensionLetters.content')
  declare allLetters: unknown[];

  @computed('approvals.@each.{declined,reason}')
  get approvalDeclined() {
    return !!this.approvals.find(
      (approval) =>
        approval.declined && approval.reason !== ApprovalReason.EXTENSION,
    );
  }

  @computed('category')
  get displayCategory() {
    if (this.category > SuspensionCategory.SUSPENSION) {
      return SuspensionCategory.getLabel(SuspensionCategory.SUSPENSION);
    }
    return SuspensionCategory.getLabel(this.category);
  }

  @computed('endDate', 'earlyReturnDate')
  get displayEndDate() {
    return this.earlyReturnDate || this.endDate;
  }

  @computed('reason')
  get displayReason() {
    switch (this.reason) {
      case SuspensionReason.OTHER:
      case SuspensionReason.OTHER___BEHAVIOUR_OF_CONCERN:
      case SuspensionReason.OTHER___SERIOUS_BEHAVIOUR_OF_CONCERN:
        return this.writtenReason || SuspensionReason.getLabel(this.reason);
      default:
        return SuspensionReason.getLabel(this.reason);
    }
  }

  @computed(
    'category',
    'endDate',
    'approvalDeclined',
    'needsApproval',
    'approvals.@each.approved',
    'earlyReturnDate',
    'extended',
    'isExpulsionSuspension',
  )
  get tab() {
    if (!this.isExpulsionSuspension) {
      switch (this.category) {
        case 0:
          return SuspensionTabs.PENDING;
        case SuspensionCategory.FORMAL_CAUTION:
          return SuspensionTabs.FORMAL_CAUTIONS;
      }
    }
    if (this.approvalDeclined) {
      return SuspensionTabs.PENDING;
    }
    if (this.needsApproval) {
      // only initial approvals need to wait in the awaiting approval tab
      const acceptedApprovals = this.approvals.find(
        (approval) => approval.approved,
      );
      if (isEmpty(acceptedApprovals)) {
        return SuspensionTabs.PENDING;
      }
    }
    if (
      moment(this.endDate, 'YYYY-MM-DD').isBefore(moment(), 'day') ||
      (this.earlyReturnDate &&
        moment(this.earlyReturnDate, 'YYYY-MM-DD').isBefore(moment(), 'day'))
    ) {
      return SuspensionTabs.COMPLETED_SUSPENSIONS;
    }
    if (this.extended) {
      return SuspensionTabs.EXTENDED_SUSPENSIONS;
    }
    return SuspensionTabs.SUSPENSIONS;
  }

  @computed('isExpulsionSuspension', 'startDate', 'endDate', 'earlyReturnDate')
  get state() {
    if (this.isExpulsionSuspension) {
      return SuspensionState.LINKED_EXPULSION;
    }
    if (!this.startDate && !this.endDate) {
      return SuspensionState.UNKNOWN;
    }

    const today = moment().format('YYYY-MM-DD');
    if (moment(this.startDate, 'YYYY-MM-DD').isAfter(today, 'day')) {
      return SuspensionState.PENDING;
    }

    if (moment(this.endDate, 'YYYY-MM-DD').isBefore(today, 'day')) {
      return SuspensionState.FINALISED;
    }

    if (
      this.earlyReturnDate &&
      moment(this.earlyReturnDate, 'YYYY-MM-DD').isBefore(today, 'day')
    ) {
      return SuspensionState.FINALISED;
    }

    return SuspensionState.IN_PROGRESS;
  }

  @computed('learner.learner.contacts.[]', 'caregivers.content.[]')
  get learnerContacts() {
    const caregivers = resolved(this.caregivers);
    // TS-STANDIN(proto people.Contact): raw attr holds plain JSON.
    return (
      this.learner.get('learner.contacts') as {
        notes: string;
        type: number;
        caregiverId: string;
      }[]
    )
      .filter((contact) => {
        const notes = contact.notes.toLowerCase();
        if (notes === 'deceased') {
          return false;
        }
        if (notes === 'caseworker' && contact.type === ContactType.EMERGENCY) {
          return true;
        }
        return (
          contact.type === ContactType.PRIMARY ||
          contact.type === ContactType.SECONDARY
        );
      })
      .map((contact) => {
        return this.store.peekRecord<Person>('person', contact.caregiverId);
      })
      .filter((caregiver) => {
        // TS-BUG: peekRecord gives null for an unloaded caregiver, and the
        // null stays in the list.
        return !caregivers.includes(caregiver!);
      });
  }

  @computed('reviewDate')
  get reviewDateHasPassed() {
    if (this.reviewDate) {
      return moment(this.reviewDate).isSameOrBefore(moment());
    }
    return false;
  }

  @computed('displayEndDate')
  get nextSchoolDay() {
    if (this.displayEndDate === '') {
      return '';
    }
    // TS-STANDIN(behaviour-dates): service is JS, so getNextSchoolDay gives
    // any.
    return this.behaviourDates.getNextSchoolDay(
      moment(this.displayEndDate, 'YYYY-MM-DD'),
      1,
    ) as string;
  }

  @computed('state', 'expulsion.id')
  get canBeExtended() {
    // expulsion suspensions cannot be interacted with like normal suspensions
    if (!isEmpty(this.get('expulsion.id'))) {
      return false;
    }

    return this.state > SuspensionState.PENDING;
  }

  @computed('canBeExtended', 'extensions.[]')
  get showExtensionManager() {
    return this.canBeExtended || this.extensions.length > 0;
  }

  // TS-BUG: this replaces Model.isValid, so the record state no longer shows
  // server validation errors (see HE-161).
  @computed(
    'tab',
    'caregivers.length',
    'behaviourDetails',
    'behaviourExpectations',
    'nominatedContact.id',
  )
  // @ts-expect-error Overrides Model.isValid with a form check.
  get isValid() {
    if (this.tab === SuspensionTabs.COMPLETED_SUSPENSIONS) {
      return true;
    }

    // eslint-disable-next-line @typescript-eslint/no-misused-promises -- An async hasMany is always truthy.
    if (!this.caregivers || this.caregivers.length === 0) {
      return false;
    }

    return (
      this.behaviourDetails &&
      this.behaviourExpectations &&
      !isEmpty(this.get('nominatedContact.id'))
    );
  }
}
